import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Avatar,
  Dropdown,
  EnumDropdownItemVariant,
  Icon,
  type IDropdownItem,
  type IDropdownProps,
} from 'shared/ui'
import { type CannedMessage } from 'entities/CannedMessages/model/CannedMessage'
import { useSavedRepliesContext } from 'pages/settings/pages/savedReplies/context/savedRepliesContext'

type ISavedRepliesActionsProps = {
  savedReply: CannedMessage
  sizeTriggerButton?: IDropdownProps['sizeTriggerButton']
  placement?: IDropdownProps['placement']
  tooltipParentElement?: HTMLDivElement | null
  collapsed?: boolean
  variantTriggerButton?: IDropdownProps['variantTriggerButton']
  excludeItemsIds?: (string | number)[]
}

export const SavedRepliesActions: FC<ISavedRepliesActionsProps> = observer(
  ({
    savedReply,
    sizeTriggerButton = 'small',
    placement = 'bottom-start',
    tooltipParentElement,
    collapsed,
    variantTriggerButton,
    excludeItemsIds = [],
  }) => {
    const store = useSavedRepliesContext()
    const {
      handleDuplicateSavedReplies,
      handleChangeVisibilitySavedReplies,
      handleDeleteSavedReplies,
      handleFavoriteSavedReplies,
      canManageAllSavedReplies,
      canManageSavedReplies,
      savedRepliesPermissionError,
      setSelectSavedReply,
      entitiesStore: {
        usersStore: { user },
      },
    } = store

    const replyOwner = store.entitiesStore.usersStore.getItem(savedReply.owner_id)
    const visibilityText = savedReply.isMyReply
      ? 'Myself only'
      : replyOwner
      ? `${replyOwner.full_name} only`
      : 'Deleted user only'

    const visibilityIcon = savedReply.isMyReply ? (
      user?.avatarInfo ? (
        <Avatar info={user.avatarInfo} size={16} />
      ) : (
        <Icon icon={'userLine'} />
      )
    ) : replyOwner?.avatarInfo ? (
      <Avatar info={replyOwner.avatarInfo} size={16} />
    ) : (
      <Icon icon={'userLine'} />
    )

    const ownerItems: IDropdownProps['items'] = [
      {
        id: 'favorite',
        label: savedReply.is_favorite ? 'Unfavorite' : 'Favorite',
        iconL: savedReply.is_favorite ? 'starFilled1' : 'starLine',
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        iconL: 'copy',
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
        divider: true,
      },

      {
        id: 'visibility',
        wrapLabel: 'Visibility',
        label: savedReply.public ? 'All members' : visibilityText,
        iconL: savedReply.public ? 'users' : undefined,
        customIconL: user && !savedReply.public ? visibilityIcon : undefined,
        iconR: !canManageSavedReplies ? undefined : 'chevronRight',
        disabled: !canManageSavedReplies,
        divider: true,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
        children: !canManageSavedReplies
          ? undefined
          : [
              {
                id: 'personal',
                label: visibilityText,
                customIconL: user ? visibilityIcon : undefined,
                activeValue: !savedReply.public,
                variant: EnumDropdownItemVariant.CheckedRight,
              },
              {
                id: 'everyone',
                label: 'All members',
                iconL: 'users',
                activeValue: savedReply.public,
                variant: EnumDropdownItemVariant.CheckedRight,
              },
            ],
      },
      {
        id: 'delete',
        label: 'Delete',
        iconL: 'delete',
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
      },
    ]

    const teamItems: IDropdownProps['items'] = [
      {
        id: 'favorite',
        label: savedReply.is_favorite ? 'Unfavorite' : 'Favorite',
        iconL: savedReply.is_favorite ? 'starFilled1' : 'starLine',
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        iconL: 'copy',
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
      },
    ]

    const onChange = (item: IDropdownItem) => {
      if (item.id === 'duplicate') {
        handleDuplicateSavedReplies(savedReply)
      }
      if (item.id === 'personal') {
        handleChangeVisibilitySavedReplies(savedReply, false)
      }
      if (item.id === 'everyone') {
        handleChangeVisibilitySavedReplies(savedReply, true)
      }
      if (item.id === 'delete') {
        handleDeleteSavedReplies(savedReply)
      }
      if (item.id === 'favorite') {
        handleFavoriteSavedReplies(savedReply)
      }
    }

    const onToggle = (value: boolean) => {
      if (value) {
        setSelectSavedReply(savedReply)
      }
    }

    if (collapsed) {
      return (
        <Dropdown
          ariaLabel={'SavedReplyActions'}
          icon={'moreHorizontal'}
          sizeTriggerButton={sizeTriggerButton}
          variantTriggerButton={variantTriggerButton}
          placement={placement}
          items={(savedReply.isMyReply || canManageAllSavedReplies ? ownerItems : teamItems).filter(
            (item) => !excludeItemsIds.includes(item.id)
          )}
          onChange={onChange}
          onToggle={onToggle}
          tooltipProps={{
            label: 'Manage',
            placement: 'top',
            PopperProps: {
              popperOptions: {
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: tooltipParentElement,
                      padding: 8,
                    },
                  },
                ],
              },
            },
          }}
        />
      )
    }

    return (
      <Dropdown
        ariaLabel={'SavedReplyActions'}
        icon={'moreHorizontal'}
        sizeTriggerButton={sizeTriggerButton}
        variantTriggerButton={variantTriggerButton}
        placement={placement}
        items={(savedReply.isMyReply || canManageAllSavedReplies ? ownerItems : teamItems).filter(
          (item) => !excludeItemsIds.includes(item.id)
        )}
        typeButton={'button'}
        buttonProps={{
          text: 'Manage',
          icon: 'settings',
          contained: 'secondary',
          size: 'medium',
        }}
        onChange={onChange}
        onToggle={onToggle}
        tooltipProps={{
          label: 'Manage',
          placement: 'top',
          PopperProps: {
            popperOptions: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: tooltipParentElement,
                    padding: 8,
                  },
                },
              ],
            },
          },
        }}
      />
    )
  }
)
